import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { LifecycleStatus } from "./types"

// F28 — single source for the lifecycle filter/facet options, previously
// duplicated byte-for-byte in DeclarationList.tsx and DeclarationListV2.tsx.
// Mirrors backend/turnus_ai/services/mc_declaration_service.py LifecycleStatus.
export const LIFECYCLE_OPTIONS: {
  value: LifecycleStatus
  labelKey: TranslationKeys
}[] = [
  { value: "pending", labelKey: "MC_DECL_LC_PENDING" },
  { value: "processing", labelKey: "MC_DECL_LC_PROCESSING" },
  { value: "ready", labelKey: "MC_DECL_LC_READY" },
  { value: "accepted", labelKey: "MC_DECL_LC_ACCEPTED" },
  { value: "expiring", labelKey: "MC_DECL_LC_EXPIRING" },
  { value: "expired", labelKey: "MC_DECL_LIFECYCLE_EXPIRED" },
  { value: "failed", labelKey: "MC_DECL_LC_FAILED" },
]

// F28 — a declaration has been approved: "accepted"/"expiring"/"expired" are
// all post-approval readings (derive_lifecycle_status only reaches them once
// approved_at is set). It has written master data naming it as evidence, so
// it can no longer be deleted (the backend 409s), and a workbench view of it
// is a static, terminal panel rather than an editable one. Previously
// reimplemented as the same three-way `!==`/`===` chain in DeclarationList.tsx,
// DeclarationListV2.tsx, DeclarationPanel.tsx and StudioV2Layout.tsx.
export const POST_APPROVAL_LIFECYCLE_STATUSES: ReadonlySet<LifecycleStatus> =
  new Set(["accepted", "expiring", "expired"])

// A type predicate (not a plain boolean return) so a caller's own
// `const isX = isPostApproval(status)` still narrows `status` on later use —
// StudioV2Layout's AlreadyApprovedPanel needs exactly that.
export function isPostApproval(
  status: LifecycleStatus | undefined,
): status is "accepted" | "expiring" | "expired" {
  return status !== undefined && POST_APPROVAL_LIFECYCLE_STATUSES.has(status)
}
